/*
 * Filter the collaborations that meet the navigatorFilterSettings. This is ALL the collaborations
 * that meet the filter criteria, not just the ones on the selected canvas.
 * @sig _filteredCollaborations :: State -> [Collaboration]
 * memoized as filteredCollaborations below
 */
import { arrayToLookupTable, memoizeReduxState } from '@range.io/functional'
import CanvasSelectors from './persisted-state/canvas-slice/canvas-selectors.js'
import CollaborationSelectors from './persisted-state/collaboration-slice/collaboration-selectors.js'
import GeometrySelectors from './persisted-state/geometry-slice/geometry-selectors.js'
import UploadSelectors from './persisted-state/upload-slice/upload-selectors.js'

const _isDateInRange = (queryDate, dateRange) =>
    queryDate.getTime() >= dateRange[0].getTime() && queryDate.getTime() <= dateRange[1].getTime()

/*
 * Filter the MediaView items that meet the mediaViewFilterSettings. This is ALL the collaborations
 * that meet the filter criteria.
 * @sig _filteredMediaView :: State -> [Collaboration]
 * memoized as filteredMediaView below
 */
const _filteredMediaView = state => {
    const allCanvases = CanvasSelectors.canvasesAsPOJO(state)
    const allUploads = Object.values(UploadSelectors.uploadsAsPOJO(state))
    const allCollaborations = CollaborationSelectors.collaborationsAsObject(state)

    const mediaViewFilterSettings = state.mediaViewFilterSettings

    const { dateRange, canvasIds, tagIds, participantIds } = mediaViewFilterSettings

    const _getUploadCanvas = upload => {
        const parentCollaboration = allCollaborations[upload.parentId]
        const geometry = GeometrySelectors.geometryForCollaboration(state, parentCollaboration)
        return allCanvases[geometry.canvasId]
    }

    const filtered = allUploads.filter(upload => {
        const filteredByDate = !dateRange.length || _isDateInRange(upload.createdAt, dateRange)

        const filteredByCanvas = !canvasIds.length || canvasIds.includes(_getUploadCanvas(upload).id)

        const tagsCombined = [...upload.tagIds, ...allCollaborations[upload?.parentId]?.tagNameIds]

        const filteredByTags = !tagIds.length || tagIds.some(tagId => tagsCombined.includes(tagId))

        const filteredByAssignee = !participantIds.length || participantIds.includes(upload.createdById)

        return filteredByDate && filteredByCanvas && filteredByTags && filteredByAssignee
    })

    return arrayToLookupTable('id', filtered)
}

/*
 * Memoize the _filteredMediaView function, recomputing only if any of the collections or filters has changed
 */
const filteredMediaView = memoizeReduxState(
    [
        'mediaViewFilterSettings',

        'canvases',
        'collaborations',
        'uploads', // we need this so when upload image URLs get updated this also gets refreshed
    ],
    _filteredMediaView
)

export { filteredMediaView }
